import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { gsap } from "gsap";
import { MessageCircle, Minimize2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useMessaging } from "../../contexts/MessagingContext";
import { getTotalUnreadCount } from "../../utils/messageUtils";
import { useViewport } from "../../hooks/useViewport";
import { useScrollLock } from "../../hooks/useScrollLock";
import { Z_INDEX, CHAT_WIDGET_SIZING } from "../../utils/constants";
import MessagingDrawer from "./MessagingDrawer";

type WidgetState = "minimized" | "maximized" | "hidden";

const STORAGE_KEY = "chatWidgetState";

interface ChatWidgetProps {
  onStateChange?: (state: WidgetState) => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ onStateChange }) => {
  const { currentUser } = useAuth();
  const { conversations } = useMessaging();
  const viewport = useViewport();
  const [searchParams] = useSearchParams();
  const [widgetState, setWidgetState] = useState<WidgetState>("minimized");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Lock scroll when maximized on mobile
  useScrollLock(widgetState === "maximized" && viewport.isMobile);

  // Check URL params for opening widget
  useEffect(() => {
    if (!currentUser) return;

    const hasMessagingParam = searchParams.get("messaging") === "true";
    const hasSellerParam = searchParams.has("seller");
    
    if (hasMessagingParam || hasSellerParam) {
      setWidgetState("maximized");
      setIsDrawerOpen(true);
    }
  }, [searchParams, currentUser]);

  // Listen for custom event to open widget
  useEffect(() => {
    const handleOpenWidget = () => {
      if (currentUser) {
        setWidgetState("maximized");
        setIsDrawerOpen(true);
      }
    };

    window.addEventListener('openChatWidget', handleOpenWidget);
    return () => {
      window.removeEventListener('openChatWidget', handleOpenWidget);
    };
  }, [currentUser]);

  // Load saved state from localStorage
  useEffect(() => {
    if (!currentUser) {
      setWidgetState("hidden");
      return;
    }

    // Don't override if URL params are present
    const hasMessagingParam = searchParams.get("messaging") === "true";
    const hasSellerParam = searchParams.has("seller");
    if (hasMessagingParam || hasSellerParam) {
      return; // URL params handled above
    }

    const savedState = localStorage.getItem(STORAGE_KEY) as WidgetState | null;
    if (savedState && (savedState === "minimized" || savedState === "maximized")) {
      setWidgetState(savedState);
      setIsDrawerOpen(savedState === "maximized");
    } else {
      // Default to minimized for new users
      setWidgetState("minimized");
      setIsDrawerOpen(false);
    }
  }, [currentUser, searchParams]);

  // Save state to localStorage and notify parent
  useEffect(() => {
    if (currentUser && widgetState !== "hidden") {
      localStorage.setItem(STORAGE_KEY, widgetState);
      onStateChange?.(widgetState);
    }
  }, [widgetState, currentUser, onStateChange]);

  // Calculate total unread messages
  const totalUnread = getTotalUnreadCount(conversations, currentUser?.uid || "");

  // Handle minimize
  const handleMinimize = useCallback(() => {
    setWidgetState("minimized");
    setIsDrawerOpen(false);
  }, []);

  // Handle maximize
  const handleMaximize = useCallback(() => {
    setWidgetState("maximized");
    setIsDrawerOpen(true);
  }, []);

  // Animate bubble on mount/unread changes
  useEffect(() => {
    if (widgetState === "minimized" && bubbleRef.current && totalUnread > 0) {
      // Pulse animation when there are unread messages
      gsap.to(bubbleRef.current, {
        scale: 1.1,
        duration: 0.3,
        yoyo: true,
        repeat: 1,
        ease: "power2.inOut",
      });
    }
  }, [totalUnread, widgetState]);

  // Animate widget expand/collapse
  useEffect(() => {
    if (widgetRef.current && viewport.isDesktop) {
      if (widgetState === "maximized") {
        widgetRef.current.style.display = "flex";
        gsap.fromTo(
          widgetRef.current,
          { scale: 0.8, opacity: 0, y: 20 },
          { scale: 1, opacity: 1, y: 0, duration: 0.3, ease: "back.out(1.7)" }
        );
      } else if (widgetState === "minimized") {
        gsap.to(widgetRef.current, {
          scale: 0.8,
          opacity: 0,
          y: 20,
          duration: 0.2,
          ease: "power2.in",
          onComplete: () => {
            if (widgetRef.current) {
              widgetRef.current.style.display = "none";
            }
          },
        });
      }
    }
  }, [widgetState, viewport.isDesktop]);

  // Don't render if user is not logged in
  if (!currentUser || widgetState === "hidden") {
    return null;
  }

  // Mobile: Show bubble button when minimized, full-screen drawer when maximized
  if (viewport.isMobile) {
    return (
      <>
        {/* Minimized Bubble Button - Mobile */}
        {widgetState === "minimized" && (
          <button
            ref={bubbleRef}
            onClick={handleMaximize}
            className="fixed bottom-20 right-4 z-[50] w-[56px] h-[56px] bg-[#da2516] rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center"
            style={{
              zIndex: Z_INDEX.DRAWER,
              bottom: `calc(64px + ${CHAT_WIDGET_SIZING.BOTTOM_MARGIN}px + env(safe-area-inset-bottom, 0px))`, // Above mobile nav (64px) + margin
            }}
            aria-label="Open messages"
          >
            <MessageCircle className="w-6 h-6 text-white" />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-white text-[#da2516] text-xs font-bold rounded-full flex items-center justify-center px-1 border-2 border-[#da2516]">
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </button>
        )}

        {/* Full-screen drawer when maximized */}
        {widgetState === "maximized" && (
          <MessagingDrawer
            isOpen={true}
            onClose={handleMinimize}
          />
        )}
      </>
    );
  }

  // Desktop: Show widget with minimized/maximized states
  return (
    <>
      {/* Minimized Bubble Button */}
      {widgetState === "minimized" && (
        <button
          ref={bubbleRef}
          onClick={handleMaximize}
          className="fixed bottom-5 right-5 z-[50] w-[60px] h-[60px] bg-[#da2516] rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
          style={{
            zIndex: Z_INDEX.DRAWER,
          }}
          aria-label="Open messages"
        >
          <MessageCircle className="w-7 h-7 text-white" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] bg-white text-[#da2516] text-xs font-bold rounded-full flex items-center justify-center px-1 border-2 border-[#da2516]">
              {totalUnread > 9 ? "9+" : totalUnread}
            </span>
          )}
          {/* Tooltip on hover */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            Messages
            {totalUnread > 0 && ` (${totalUnread} new)`}
          </div>
        </button>
      )}

      {/* Maximized Widget Window */}
      {widgetState === "maximized" && (
        <div
          ref={widgetRef}
          className="fixed bottom-5 right-5 z-[50] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col border border-gray-200"
          style={{
            width: `${CHAT_WIDGET_SIZING.DESKTOP_WIDTH}px`,
            height: `${CHAT_WIDGET_SIZING.DESKTOP_HEIGHT}px`,
            maxHeight: `calc(100vh - ${CHAT_WIDGET_SIZING.BOTTOM_MARGIN * 2}px)`,
            zIndex: Z_INDEX.DRAWER,
            display: "flex",
          }}
        >
          {/* Widget Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-[#da2516] text-white">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <h3 className="font-semibold text-sm">Messages</h3>
              {totalUnread > 0 && (
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {totalUnread} new
                </span>
              )}
            </div>
            <button
              onClick={handleMinimize}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Minimize"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
          </div>

          {/* Messaging Drawer Content - Widget mode */}
          <div className="flex-1 overflow-hidden relative">
            <MessagingDrawer
              isOpen={true}
              onClose={handleMinimize}
              widgetMode={true}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;

